import { ReactElement, useEffect, useState } from 'react'
import { IndexesFormInputsProps, IndexesFormType } from './types'
import { useTranslation } from 'react-i18next'
import { ProductType, Register } from 'types/types'
import { useFormContext } from 'react-hook-form'
import { enumKeys } from 'utils/objects'
import { MeterReadingSource } from 'types/contracts'
import styles from './IndexesFormInputs.module.scss'
import { EXTRA_CONSUMPTION_LIMIT_EL, EXTRA_CONSUMPTION_LIMIT_GAS } from 'pages/app/billing/billing-cycles/constants'
import { Heading, Icon, TextField } from '@boltenergy-be/design-system'
import useGetCurrentContractMeterReadings from 'hooks/useGetCurrentContractMeterReadings.ts'
import { Direction, DIRECTIONS, TIMEFRAME_CODES, TimeframeCode } from '@boltenergy-be/shared'

const IndexesFormsInputs = ({
  serviceContracts,
  shouldRenderAllInputsIfNoPreviousIndexes,
  showEan = false,
  showLastMeterReadingByDgo = false,
  explicitRequired = false,
  setLowerCheck,
  setHigherCheck
}: IndexesFormInputsProps) => {
  // Meter readings
  const { data: meterReadings, isLoading } = useGetCurrentContractMeterReadings({ customSkip: false })

  // React hook form context
  const {
    register,
    formState: { errors }
  } = useFormContext<{ indexes: IndexesFormType }>()

  // i18n
  const { t } = useTranslation(['common', 'validation'])

  // Local state
  const [indexStateCheck, setIndexStateCheck] = useState<{ [key: string]: 'LOW' | 'HIGH' | 'OK' }>({})

  // Constants
  const { electricity, gas } = serviceContracts
  const hidden = !electricity.deliveryPoint.previousIndex && !shouldRenderAllInputsIfNoPreviousIndexes

  /**
   * Returns the correct meter reading entry based on the given product type & timeframe code
   *
   * @param {ProductType} type
   * @param {Direction} direction
   * @param {TimeframeCode} timeframeCode
   * @returns {number|undefined}
   */
  const getMeterReadingEntry = (type: ProductType, direction: Direction, timeframeCode: TimeframeCode): number | undefined => {
    if (type === ProductType.GAS) return meterReadings?.gas?.consumption?.singleRate

    switch (timeframeCode) {
      case TIMEFRAME_CODES.high:
        return meterReadings?.electricity?.[direction === DIRECTIONS.consumption ? 'consumption' : 'injection']?.doubleRate?.day

      case TIMEFRAME_CODES.low:
        return meterReadings?.electricity?.[direction === DIRECTIONS.consumption ? 'consumption' : 'injection']?.doubleRate?.night

      case TIMEFRAME_CODES.nightExclusive:
        return meterReadings?.electricity?.[direction === DIRECTIONS.consumption ? 'consumption' : 'injection']?.exclNight

      default:
        return meterReadings?.electricity?.[direction === DIRECTIONS.consumption ? 'consumption' : 'injection']?.singleRate
    }
  }

  /**
   * Checks if one of the given values is too high or low
   */
  useEffect(() => {
    const states = Object.values(indexStateCheck)
    if (states.every((value) => value === 'OK')) {
      setLowerCheck?.(false)
      setHigherCheck?.(false)
    } else {
      setLowerCheck?.(states.some((value) => value === 'LOW'))
      setHigherCheck?.(states.some((value) => value === 'HIGH'))
    }
  }, [indexStateCheck, setHigherCheck, setLowerCheck])

  /**
   * Returns the register form group for the given type, register & index
   *
   * @param {ProductType} type
   * @param {Direction} direction
   * @param {TimeframeCode} timeframeCode
   * @param {number} index
   * @param {boolean} required
   * @returns {ReactElement}
   */
  const getRegisterFormGroup = (
    type: ProductType,
    direction: Direction,
    timeframeCode: TimeframeCode,
    index: number,
    required: boolean
  ): ReactElement => {
    const isGas = type === ProductType.GAS

    return (
      <div className="form-group" key={index}>
        <label htmlFor={`indexes.${type}.${index}`} className={styles['index-label']}>
          <span>
            {t(($) => (direction === DIRECTIONS.production ? $.injection : $[type]))}{' '}
            {timeframeCode !== TIMEFRAME_CODES.totalHour && t(($) => $.indexes[timeframeCode as 'HIGH' | 'LOW' | 'NIGHT_EXCLUSIVE'])}
            {!isGas && (
              <>
                {([TIMEFRAME_CODES.low, TIMEFRAME_CODES.nightExclusive] as TimeframeCode[]).includes(timeframeCode) ? (
                  <Icon name="moon" />
                ) : (
                  <Icon name="sun" />
                )}
              </>
            )}
          </span>

          {showEan && (
            <span className={styles.ean}>
              {t(($) => $.ean)}: {(isGas ? gas : electricity)?.deliveryPoint?.ean}
            </span>
          )}
        </label>

        <TextField
          id={`indexes.${type}.${index}`}
          label=""
          type="number"
          step="1"
          {...register(`indexes.${type}.${index}`, {
            required: required
              ? t(($) => $.required, {
                  ns: 'validation'
                })
              : false,
            min: {
              value: 0,
              message: t(($) => $.min, {
                min: 0,
                ns: 'validation'
              })
            },
            validate: (value) =>
              !value ||
              /^\d+$/.test(String(value)) ||
              t(($) => $.invalid.meterReading, {
                ns: 'validation'
              })
          })}
          error={errors?.indexes?.[type]?.[index]?.message}
          grid="full"
          suffix={{ as: ',000', className: styles.bordered }}
          onWheel={(e) => e.currentTarget.blur()}
          onBlur={(event) => {
            if (
              showLastMeterReadingByDgo &&
              meterReadings?.[type]?.source === MeterReadingSource.EOD_DGO &&
              (setLowerCheck || setHigherCheck)
            ) {
              const meterReadingEntry = getMeterReadingEntry(type, direction, timeframeCode)
              const key = `${type}-${direction}-${timeframeCode}`
              if (event.target.valueAsNumber < (meterReadingEntry ?? 0)) {
                setIndexStateCheck({ ...indexStateCheck, [key]: 'LOW' })
              } else if (
                event.target.valueAsNumber >
                (meterReadingEntry ?? 0) + (isGas ? EXTRA_CONSUMPTION_LIMIT_GAS : EXTRA_CONSUMPTION_LIMIT_EL)
              ) {
                setIndexStateCheck({ ...indexStateCheck, [key]: 'HIGH' })
              } else {
                setIndexStateCheck({ ...indexStateCheck, [key]: 'OK' })
              }
            }
          }}
        />

        {showLastMeterReadingByDgo && !isLoading && meterReadings?.[type]?.source === MeterReadingSource.EOD_DGO && (
          <div className={styles['last-meter-reading']}>
            <span>
              <span className={styles['last-meter-reading-label']}>{t(($) => $.lastKnownMeterReading)}:</span>{' '}
              {getMeterReadingEntry(type, direction, timeframeCode)?.toFixed(0)}
            </span>
          </div>
        )}
      </div>
    )
  }

  return !hidden ? (
    <div className={styles.indexes}>
      {electricity.deliveryPoint.previousIndex ? (
        <>
          <div className={styles.electricity}>
            <Heading as="h3" variant="h6" serif>
              {t(($) => $.indexes.title)} {t(($) => $.electricity).toLowerCase()}
            </Heading>

            {electricity.deliveryPoint.previousIndex.registers
              .filter((indexRegister: Register) => indexRegister.timeframeCode !== TIMEFRAME_CODES.notUsed)
              .map((indexRegister: Register, index: number) =>
                getRegisterFormGroup(
                  ProductType.ELECTRICITY,
                  indexRegister.direction,
                  indexRegister.timeframeCode,
                  index,
                  explicitRequired ?? true
                )
              )}
          </div>

          {gas && (
            <div className={styles.gas}>
              <Heading as="h3" variant="h6" serif>
                {t(($) => $.indexes.title)} {t(($) => $.gas).toLowerCase()}
              </Heading>

              {gas?.deliveryPoint?.previousIndex?.registers
                .filter((indexRegister: Register) => indexRegister.timeframeCode !== TIMEFRAME_CODES.notUsed)
                .map((indexRegister: Register, index: number) =>
                  getRegisterFormGroup(
                    ProductType.GAS,
                    indexRegister.direction,
                    indexRegister.timeframeCode,
                    index,
                    explicitRequired ?? true
                  )
                )}
            </div>
          )}
        </>
      ) : (
        shouldRenderAllInputsIfNoPreviousIndexes && (
          <>
            <div className={styles.electricity}>
              <Heading as="h3" variant="h6" serif>
                {t(($) => $.indexes.title)} {t(($) => $.electricity).toLowerCase()}
              </Heading>

              {enumKeys(TIMEFRAME_CODES)
                .filter((key) => TIMEFRAME_CODES[key] !== TIMEFRAME_CODES.notUsed)
                .map((key, index: number) =>
                  getRegisterFormGroup(
                    ProductType.ELECTRICITY,
                    DIRECTIONS.consumption,
                    TIMEFRAME_CODES[key],
                    index,
                    explicitRequired ?? false
                  )
                )}
            </div>

            {gas && (
              <div className={styles.gas}>
                <h3>
                  {t(($) => $.indexes.title)} {t(($) => $.gas).toLowerCase()}
                </h3>

                {getRegisterFormGroup(ProductType.GAS, DIRECTIONS.consumption, TIMEFRAME_CODES.totalHour, 0, false)}
              </div>
            )}
          </>
        )
      )}
    </div>
  ) : null
}

export default IndexesFormsInputs
